import type { FichierDTO } from '@/features/api/extranet/dto/Demande/shared'
import { FetchResponseError } from '@/features/fetch'
import services from '@/features/services/demande/DemandeAllService'
import { computed, ref } from 'vue'
import { ACCEPTED_FILE_TYPES, MAX_FILE_COUNT, MAX_FILE_LENGTH } from './constants'
import type {
  NatureRecord,
  UploadErreur,
  UploadFichiersContent,
  UseUploadFichiersOptions,
} from './UploadFichiers.types'

export function useUploadFichiers(options: UseUploadFichiersOptions) {
  const { fichiers, readonly, fichierAdd, fichierRemove } = options

  const isUploadFichierOpen = ref<boolean>(true)
  const uploadErreurs = ref<UploadErreur[]>([])

  const naturesAuthorisées = computed(() => {
    const natures = new Set([
      ...options.naturesFichiersFacultatifs.value,
      ...options.naturesFichiersObligatoires.value
    ])
    return [...natures]
  })

  const naturesFichiersAjoutées = ref([...options.naturesFichiersAjoutFacultatifs.value])
  const naturesFichiersAffichées = computed(() => {
    const natures = new Set<string>([
      ...options.naturesFichiersObligatoires.value,
      ...(fichiers.value?.map((item) => item.nature!) ?? []),
      ...naturesFichiersAjoutées.value,
    ])
    return [...natures].filter(nature => naturesAuthorisées.value.includes(nature))
  })
  const naturesFichiersFacultatifs = computed(() =>
    options.naturesFichiersFacultatifs.value.filter((code) => naturesFichiersAffichées.value.includes(code) === false),
  )

  const nombreFichiersObligatoires = computed(() => {
    return options.naturesFichiersObligatoires.value.length
  })

  const nombreFichiersObligatoiresChargés = computed(() => {
    return options.naturesFichiersObligatoires.value.filter((nature) => fichiers.value.some((f) => f.nature === nature))
      .length
  })

  function getNatureLibelle(nature: string) {
    return options.lovNatureFichiers.find((item) => item.code === nature)?.intitule ?? 'Intitulé non défini'
  }

  const natureRecord = computed(() => {
    const result: NatureRecord = {}
    const toutesNatures = [...options.naturesFichiersFacultatifs.value, ...options.naturesFichiersObligatoires.value]
    for (const nature of toutesNatures) {
      const intitule = getNatureLibelle(nature)
      const obligatoire = options.naturesFichiersObligatoires.value.some((str) => str === nature)
      result[nature] = {
        code: nature,
        intitule,
        nombreMax: options.nombreFichiersMaximum.value[nature] ?? MAX_FILE_COUNT,
        nombreMin: options.nombreFichiersMinimum.value[nature] ?? 0,
        obligatoire,
        messageAvertissement: options.messagesAvertissement?.value?.[nature],
      }
    }
    return result
  })

  const natureRecordFacultatifs = computed(() => {
    return naturesFichiersFacultatifs.value.reduce((result, nature) => {
      const natureValue = natureRecord.value[nature]
      if (natureValue) {
        result[nature] = natureValue
      }
      return result
    }, {} as NatureRecord)
  })

  function ajouteNature(nature: string) {
    if (options.naturesFichiersFacultatifs.value.includes(nature) === false) return
    if (naturesFichiersAffichées.value.includes(nature)) return
    naturesFichiersAjoutées.value = [...naturesFichiersAjoutées.value, nature]
  }

  async function ajouteFichiersAsync(nature: string, fileList: FileList): Promise<void> {
    for (let index = 0; index < fileList.length; index++) {
      const file = fileList[index]
      if (!file) continue
      let messageErreur: string | null = null

      const nbFichiersNature = options.fichiers.value.filter((f) => f.nature === nature).length
      const nbFichiersMax = natureRecord.value[nature]!.nombreMax
      if (nbFichiersNature >= nbFichiersMax) {
        messageErreur = `Nombre maximal de fichiers atteint ${nbFichiersMax}`
      }

      if (!messageErreur) {
        const fileSizeInMB = file!.size / 1024 / 1024
        if (fileSizeInMB > MAX_FILE_LENGTH) {
          messageErreur = `${file?.name}" dépasse la taille maximale de ${MAX_FILE_LENGTH} Mo`
        }

        if (fileSizeInMB === 0) {
          messageErreur = `${file?.name}" est vide. Veuillez sélectionner un autre fichier.`
        }
      }

      if (!messageErreur) {
        const fileExtension = file?.name.split('.').pop()?.toLowerCase()
        if (!ACCEPTED_FILE_TYPES.includes(`.${fileExtension}`)) {
          messageErreur = `${file?.name}" n'est pas un type de fichier accepté (${fileExtension})`
        }
      }

      if (!messageErreur) {
        // Charge le document
        try {
          const id = await services.importFile(file, { dialogMode: 'silent', sablier: true })
          const fichier: FichierDTO = {
            nature,
            fichierUid: id,
            intitule: file.name,
            typeMime: null,
            creationDate: null,
            creatorUserId: null,
            metadata: null,
          }
          await fichierAdd(fichier)
        } catch (err) {
          if (err instanceof FetchResponseError) {
            messageErreur = `${err.message} (${err.status})`
          } else {
            messageErreur = `${err}`
          }
        }
      }

      if (messageErreur) {
        // Ajoute l'erreur
        const ordre = calculeOrdreAjoutErreur()

        const erreur: UploadErreur = {
          message: messageErreur,
          intitule: file!.name,
          nature,
          ordre,
        }
        uploadErreurs.value.push(erreur)
      }
    }
  }

  async function retireFichierAsync(toRemove: UploadFichiersContent) {
    if (toRemove.type === 'succes') {
      const indexFichier = options.fichiers.value.findIndex((item) => item.fichierUid === toRemove.fichier.fichierUid)
      const newUploadErreurs = uploadErreurs.value.map((item) =>
        item.ordre < indexFichier
          ? item
          : {
            ...item,
            ordre: item.ordre - 1,
          },
      )
      await fichierRemove(toRemove.fichier)
      uploadErreurs.value = newUploadErreurs
    } else {
      uploadErreurs.value = uploadErreurs.value.filter(
        (item) => (item.nature === toRemove.nature && item.ordre === toRemove.ordre) === false,
      )
    }
  }

  async function ouvreFichierAsync(value: FichierDTO) {
    if (!value.fichierUid) return
    const blob = await services.exportFile(options.typeDemande.value, options.idDemande.value, value.fichierUid)

    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    try {
      a.href = url
      a.target = '_blank'
      a.download = value.intitule!
      document.body.appendChild(a)
    } finally {
      a.click()
      a.remove()

      // Libère la mémoire après utilisation
      window.URL.revokeObjectURL(url)
    }
  }

  function calculeOrdreAjoutErreur(): number {
    const ordrePrecedent = content.value.length ? content.value.at(content.value.length - 1)!.ordre : -1
    const incrément = (Math.floor(ordrePrecedent) + 1 - ordrePrecedent) / 2
    return ordrePrecedent + incrément
  }

  async function retireNature(nature: string) {
    if (options.naturesFichiersObligatoires.value.includes(nature)) return
    for (const item of content.value.filter((item) => item.nature === nature)) {
      await retireFichierAsync(item)
    }
    naturesFichiersAjoutées.value = naturesFichiersAjoutées.value.filter((item) => item !== nature)
  }

  const content = computed(() => {
    const contentFichiers = fichiers.value.map(
      (item, index) =>
        ({
          type: 'succes',
          nature: item.nature,
          ordre: index,
          fichier: item,
        }) as UploadFichiersContent,
    )

    const contentErreurs = uploadErreurs.value.map(
      (item) =>
        ({
          type: 'echec',
          nature: item.nature,
          ordre: item.ordre,
          erreur: item,
        }) as UploadFichiersContent,
    )

    return [...contentFichiers, ...contentErreurs].sort((itemA, itemB) => itemA.ordre - itemB.ordre)
  })

  return {
    readonly: readonly,
    content,
    natureRecord,
    natureRecordFacultatifs,
    naturesFichiersAffichées,
    isUploadFichierOpen,
    nombreFichiersObligatoires,
    nombreFichiersObligatoiresChargés,
    ajouteNature,
    retireNature,
    ajouteFichiersAsync,
    ouvreFichierAsync,
    retireFichierAsync,
  }
}
